export type ChannelAccountChannel =
  | 'WhatsApp'
  | 'Instagram'
  | 'Webchat'
  | 'Telegram'
  | 'Messenger'
  | 'TikTok'
  | 'Voice'
  | 'Sms'
  | 'Email';

/** Whether each channel has an enabled account with usable credentials. */
export type ChannelReadiness = Partial<Record<ChannelAccountChannel, boolean>>;

export interface ChannelAccountWhatsAppSettings {
  cloudApiBaseUrl: string;
  cloudApiVersion: string;
  whatsAppAccountId?: string | null;
  cloudPhoneNumberId: string;
  phoneNumber?: string | null;
  hasCloudAccessToken: boolean;
  hasCloudWebhookVerifyToken: boolean;
  hasCloudAppSecret: boolean;
  validateCloudSignature: boolean;
}

export interface WhatsAppPhoneNumberRegistration {
  cloudPhoneNumberId: string;
  success: boolean;
  registeredAtUtc: string;
}

export interface WhatsAppAppSubscription {
  whatsAppAccountId: string;
  success: boolean;
  updatedAtUtc: string;
}

export interface WhatsAppSubscribedApp {
  appId: string | null;
  appName: string | null;
  appLink: string | null;
}

export interface WhatsAppSubscribedApps {
  whatsAppAccountId: string;
  items: WhatsAppSubscribedApp[];
  count: number;
  retrievedAtUtc: string;
}

export interface ChannelAccountInstagramSettings {
  apiBaseUrl: string;
  apiVersion: string;
  instagramBusinessAccountId: string;
  instagramUsername?: string | null;
  defaultCommentReplyMode: string;
  hasPageAccessToken: boolean;
}

export interface UpdateChannelAccountLimitsRequest {
  maxWhatsApp?: number;
  maxInstagram?: number;
  maxWebchat?: number;
  maxTelegram?: number;
  maxMessenger?: number;
  maxTikTok?: number;
  maxVoice?: number;
  maxEmail?: number;
  maxWhatsAppApp?: number;
  maxTikTokApp?: number;
  maxTotal?: number;
}

export interface ChannelAccountLimitsSettingsResponse {
  effective: ChannelAccountLimits;
  defaults: ChannelAccountLimits;
  hasOverrides: boolean;
}

export interface ChannelAccountCredentialStatus {
  hasAccessToken: boolean;
  hasWebhookVerifyToken: boolean;
  hasAppSecret: boolean;
  validateSignatureEnabled: boolean;
}

export interface ChannelAccount {
  id: string;
  channel: ChannelAccountChannel;
  externalAccountId: string;
  displayName: string;
  isEnabled: boolean;
  isDefault: boolean;
  createdAtUtc: string;
  updatedAtUtc: string;
  credentialStatus?: ChannelAccountCredentialStatus | null;
  connectionKind?: 'cloud' | 'app' | null;
}

export type WhatsAppConnectionKind = 'cloud' | 'app';

export const formatChannelAccountOptionLabel = (account: Pick<ChannelAccount, 'channel' | 'displayName' | 'externalAccountId' | 'connectionKind'>): string => {
  const name = account.displayName || account.externalAccountId;
  if (account.channel === 'WhatsApp') {
    const kind = account.connectionKind === 'app' ? 'App' : 'Cloud';
    return `${kind} · ${name}`;
  }

  if (account.channel === 'TikTok') {
    const kind = account.connectionKind === 'app' ? 'App' : 'Business';
    return `${kind} · ${name}`;
  }

  return name;
};

export interface ChannelAccountLimits {
  maxWhatsApp: number;
  maxInstagram: number;
  maxWebchat: number;
  maxTelegram: number;
  maxMessenger: number;
  maxTikTok: number;
  maxVoice: number;
  maxEmail: number;
  maxWhatsAppApp: number;
  maxTikTokApp: number;
  maxTotal: number;
  usedWhatsApp: number;
  usedInstagram: number;
  usedWebchat: number;
  usedTelegram: number;
  usedMessenger: number;
  usedTikTok: number;
  usedVoice: number;
  usedEmail: number;
  usedWhatsAppApp: number;
  usedTikTokApp: number;
  usedTotal: number;
}

export interface ChannelAccountListResponse {
  items: ChannelAccount[];
  limits: ChannelAccountLimits;
}

export interface UpdateChannelAccountRequest {
  displayName?: string;
  isEnabled?: boolean;
  isDefault?: boolean;
}

export interface CreateWhatsAppAppChannelAccountRequest {
  displayName: string;
  isDefault?: boolean;
}

export interface WhatsAppAppStatusResponse {
  configured: boolean;
}

export interface WhatsAppAppSession {
  account: ChannelAccount;
  connectionStatus: string;
  qrCodeBase64?: string | null;
  linkedPhoneNumber?: string | null;
  lastQrAtUtc?: string | null;
  lastDisconnectReason?: string | null;
  evolutionConfigured: boolean;
}

export interface CreateTikTokAppChannelAccountRequest {
  displayName: string;
  padCode: string;
  isDefault?: boolean;
}

export interface TikTokAppStatusResponse {
  configured: boolean;
  recommendedSku?: string;
  monthlyPriceUsd?: number;
}

export interface TikTokAppSession {
  account: ChannelAccount;
  connectionStatus: string;
  padCode: string;
  deviceSecret: string;
  ingestUrl: string;
  lastHeartbeatAtUtc?: string | null;
  lastEventAtUtc?: string | null;
  watcherSource?: string | null;
}

export interface CreateWhatsAppChannelAccountRequest {
  displayName: string;
  cloudPhoneNumberId: string;
  phoneNumber?: string;
  whatsAppAccountId?: string;
  cloudAccessToken: string;
  cloudApiBaseUrl?: string;
  cloudApiVersion?: string;
  cloudWebhookVerifyToken?: string;
  cloudAppSecret?: string;
  validateCloudSignature?: boolean;
  isDefault?: boolean;
}

export interface UpdateWhatsAppChannelAccountCredentialsRequest {
  cloudPhoneNumberId?: string;
  phoneNumber?: string;
  whatsAppAccountId?: string;
  cloudAccessToken?: string;
  cloudApiBaseUrl?: string;
  cloudApiVersion?: string;
  cloudWebhookVerifyToken?: string;
  cloudAppSecret?: string;
  validateCloudSignature?: boolean;
}

export interface CreateInstagramChannelAccountRequest {
  displayName: string;
  instagramBusinessAccountId: string;
  pageAccessToken: string;
  apiBaseUrl?: string;
  apiVersion?: string;
  instagramUsername?: string;
  defaultCommentReplyMode?: string;
  isDefault?: boolean;
}

export interface UpdateInstagramChannelAccountCredentialsRequest {
  instagramBusinessAccountId?: string;
  pageAccessToken?: string;
  apiBaseUrl?: string;
  apiVersion?: string;
  instagramUsername?: string;
  defaultCommentReplyMode?: string;
}

export interface ChannelAccountTelegramSettings {
  apiBaseUrl: string;
  hasBotToken: boolean;
  hasWebhookSecretToken: boolean;
}

export interface ChannelAccountMessengerSettings {
  apiBaseUrl: string;
  apiVersion: string;
  pageId: string;
  hasPageAccessToken: boolean;
  hasWebhookVerifyToken: boolean;
  hasAppSecret: boolean;
  validateSignature: boolean;
}

export interface ChannelAccountTikTokSettings {
  apiBaseUrl: string;
  businessOpenId: string;
  appId?: string | null;
  hasAccessToken: boolean;
  hasAppSecret: boolean;
  validateSignature: boolean;
}

export interface CreateTelegramChannelAccountRequest {
  displayName: string;
  botToken: string;
  webhookSecretToken?: string;
  apiBaseUrl?: string;
  isDefault?: boolean;
}

export interface UpdateTelegramChannelAccountCredentialsRequest {
  botToken?: string;
  webhookSecretToken?: string;
  apiBaseUrl?: string;
}

export interface CreateMessengerChannelAccountRequest {
  displayName: string;
  pageId: string;
  pageAccessToken: string;
  apiBaseUrl?: string;
  apiVersion?: string;
  webhookVerifyToken?: string;
  appSecret?: string;
  validateSignature?: boolean;
  isDefault?: boolean;
}

export interface UpdateMessengerChannelAccountCredentialsRequest {
  pageId?: string;
  pageAccessToken?: string;
  apiBaseUrl?: string;
  apiVersion?: string;
  webhookVerifyToken?: string;
  appSecret?: string;
  validateSignature?: boolean;
}

export interface CreateTikTokChannelAccountRequest {
  displayName: string;
  businessOpenId: string;
  accessToken: string;
  appId?: string;
  appSecret?: string;
  apiBaseUrl?: string;
  validateSignature?: boolean;
  isDefault?: boolean;
}

export interface UpdateTikTokChannelAccountCredentialsRequest {
  businessOpenId?: string;
  accessToken?: string;
  appId?: string;
  appSecret?: string;
  apiBaseUrl?: string;
  validateSignature?: boolean;
}

export interface VoiceAgentConnectorSettings {
  enabled: boolean;
  answerMode: string;
  answeredNumbers?: string | null;
  handoffTarget?: string | null;
  greeting?: string | null;
  businessHoursStart?: string | null;
  businessHoursEnd?: string | null;
  businessHoursTimeZone?: string | null;
  businessHoursWeekdays?: string | null;
  hasTwilioAccountSid: boolean;
  hasTwilioAuthToken: boolean;
  twilioFromNumber?: string | null;
  incomingWebhookUrl?: string | null;
  statusCallbackUrl?: string | null;
  /** Answer calls 3CX routes to the API client's Route Point, with no Twilio leg. */
  threeCxEnabled?: boolean;
  /** Explicit Route Point DN override; empty means the Call Control client id is used. */
  threeCxRoutePointDn?: string | null;
  /** The DN the backend actually listens on (override or client id). */
  threeCxEffectiveRoutePointDn?: string | null;
}

export interface ChannelAccountVoiceSettings {
  serverUrl: string;
  hasClientId: boolean;
  hasClientSecret: boolean;
  inboundEnabled: boolean;
  outboundEnabled: boolean;
  defaultCallerId?: string | null;
  provider: string;
  voiceAgent?: VoiceAgentConnectorSettings | null;
}

export interface CreateVoiceChannelAccountRequest {
  displayName: string;
  serverUrl: string;
  clientId: string;
  clientSecret: string;
  inboundEnabled?: boolean;
  outboundEnabled?: boolean;
  defaultCallerId?: string;
  isDefault?: boolean;
}

export interface UpdateVoiceChannelAccountCredentialsRequest {
  serverUrl?: string;
  clientId?: string;
  clientSecret?: string;
  inboundEnabled?: boolean;
  outboundEnabled?: boolean;
  defaultCallerId?: string;
  voiceAgent?: {
    enabled?: boolean;
    answerMode?: string;
    answeredNumbers?: string;
    handoffTarget?: string;
    greeting?: string;
    businessHoursStart?: string;
    businessHoursEnd?: string;
    businessHoursTimeZone?: string;
    businessHoursWeekdays?: string;
    twilioAccountSid?: string;
    twilioAuthToken?: string;
    twilioFromNumber?: string;
    threeCxEnabled?: boolean;
    threeCxRoutePointDn?: string;
  };
}

export interface ChannelAccountSmsSettings {
  apiUrl: string;
  httpMethod: string;
  contentType: string;
  bodyTemplate?: string | null;
  authHeaderName?: string | null;
  hasAuthHeaderValue: boolean;
  basicAuthUsername?: string | null;
  hasBasicAuthPassword: boolean;
  senderName?: string | null;
  successContains?: string | null;
  outboundEnabled: boolean;
}

export interface CreateSmsChannelAccountRequest {
  displayName: string;
  apiUrl: string;
  httpMethod?: string;
  contentType?: string;
  bodyTemplate?: string;
  authHeaderName?: string;
  authHeaderValue?: string;
  basicAuthUsername?: string;
  basicAuthPassword?: string;
  senderName?: string;
  successContains?: string;
  outboundEnabled?: boolean;
  isDefault?: boolean;
}

export interface UpdateSmsChannelAccountCredentialsRequest {
  apiUrl?: string;
  httpMethod?: string;
  contentType?: string;
  bodyTemplate?: string;
  authHeaderName?: string;
  authHeaderValue?: string;
  basicAuthUsername?: string;
  basicAuthPassword?: string;
  senderName?: string;
  successContains?: string;
  outboundEnabled?: boolean;
}

export type EmailOutboundProvider = 'smtp' | 'sendgrid' | 'mailgun' | 'postmark' | 'resend';
export type EmailInboundMode = 'none' | 'imap' | 'webhook';

/** Providers that can push inbound mail to `/api/v1/email/webhook/{provider}/{accountId}`. */
export const EMAIL_INBOUND_WEBHOOK_PROVIDERS = ['sendgrid', 'mailgun', 'postmark', 'raw'] as const;
export type EmailInboundWebhookProvider = (typeof EMAIL_INBOUND_WEBHOOK_PROVIDERS)[number];
export type EmailTransportSecurity = 'none' | 'starttls' | 'ssl';
export type EmailReplyFormat = 'html' | 'text';

export interface EmailSmtpSettings {
  host: string;
  port: number;
  security: EmailTransportSecurity;
  username?: string | null;
  hasPassword: boolean;
  timeoutSeconds: number;
}

export interface EmailApiSettings {
  hasApiKey: boolean;
  baseUrl?: string | null;
  domain?: string | null;
}

export interface EmailImapSettings {
  host: string;
  port: number;
  security: EmailTransportSecurity;
  username?: string | null;
  hasPassword: boolean;
  folder: string;
  processedFolder?: string | null;
  pollIntervalSeconds: number;
  markAsRead: boolean;
}

export interface EmailBehaviourSettings {
  replyFormat: EmailReplyFormat;
  subjectPrefix?: string | null;
  defaultSubject?: string | null;
  signature?: string | null;
  quoteOriginal: boolean;
  threadIdleDays: number;
  maxAttachmentSizeMb: number;
  useBrandLayout: boolean;
  /** Saved email layout for this mailbox; null means the workspace default layout. */
  layoutId: string | null;
  /** Agents' personal signatures replace the mailbox signature on replies they send. */
  useAgentSignatures: boolean;
}

export interface EmailFilterSettings {
  allowedSenderDomains: string[];
  blockedSenderDomains: string[];
  ignoreAutoGenerated: boolean;
  ignoreSubjectPattern?: string | null;
}

export interface ChannelAccountEmailSettings {
  address: string;
  fromName?: string | null;
  replyTo?: string | null;
  bccCopyAddress?: string | null;
  outboundProvider: EmailOutboundProvider;
  outboundEnabled: boolean;
  smtp: EmailSmtpSettings;
  api: EmailApiSettings;
  inboundMode: EmailInboundMode;
  inboundEnabled: boolean;
  imap: EmailImapSettings;
  hasInboundWebhookSecret: boolean;
  behaviour: EmailBehaviourSettings;
  filters: EmailFilterSettings;
  isOutboundConfigured: boolean;
  isInboundConfigured: boolean;
  inboundState?: EmailInboundState | null;
  oauth: EmailOAuthSettings;
}

export type EmailOAuthProvider = 'google' | 'microsoft';

/** OAuth (XOAUTH2) sign-in state for Gmail / Microsoft 365 mailboxes. */
export interface EmailOAuthSettings {
  provider: EmailOAuthProvider | null;
  isConnected: boolean;
  account?: string | null;
  connectedAtUtc?: string | null;
  hasClientId: boolean;
  hasClientSecret: boolean;
  clientId?: string | null;
  directoryTenant?: string | null;
}

export interface EmailOAuthSettingsInput {
  provider?: string;
  clientId?: string;
  clientSecret?: string;
  directoryTenant?: string;
}

export interface EmailOAuthProviderStatus {
  id: EmailOAuthProvider;
  label: string;
  platformAppConfigured: boolean;
  scopes: string[];
}

export interface EmailOAuthProvidersResponse {
  redirectUri: string;
  providers: EmailOAuthProviderStatus[];
}

export interface EmailOAuthStartResponse {
  authorizeUrl: string;
  redirectUri: string;
}

export type EmailDnsCheckStatus = 'pass' | 'warn' | 'fail' | 'unknown';

export interface EmailDnsCheck {
  id: string;
  status: EmailDnsCheckStatus;
  summary: string;
  detail?: string | null;
  records: string[];
  recommendation?: string | null;
}

export interface EmailDnsDiagnostics {
  domain: string;
  outboundProvider: string;
  checkedAtUtc: string;
  overallStatus: EmailDnsCheckStatus;
  checks: EmailDnsCheck[];
}

/** Progress of the background IMAP poller for one mailbox; null until the first poll. */
export interface EmailInboundState {
  lastPolledAtUtc?: string | null;
  lastImportedAtUtc?: string | null;
  lastError?: string | null;
  lastUid?: number | null;
}

export interface EmailSmtpSettingsInput {
  host?: string;
  port?: number;
  security?: string;
  username?: string;
  password?: string;
  timeoutSeconds?: number;
}

export interface EmailApiSettingsInput {
  apiKey?: string;
  baseUrl?: string;
  domain?: string;
}

export interface EmailImapSettingsInput {
  host?: string;
  port?: number;
  security?: string;
  username?: string;
  password?: string;
  folder?: string;
  processedFolder?: string;
  pollIntervalSeconds?: number;
  markAsRead?: boolean;
}

export interface EmailBehaviourSettingsInput {
  replyFormat?: string;
  subjectPrefix?: string;
  defaultSubject?: string;
  signature?: string;
  quoteOriginal?: boolean;
  threadIdleDays?: number;
  maxAttachmentSizeMb?: number;
  useBrandLayout?: boolean;
  /** Empty string clears the override back to the workspace default. */
  layoutId?: string;
  useAgentSignatures?: boolean;
}

export interface EmailFilterSettingsInput {
  allowedSenderDomains?: string[];
  blockedSenderDomains?: string[];
  ignoreAutoGenerated?: boolean;
  ignoreSubjectPattern?: string;
}

export interface CreateEmailChannelAccountRequest {
  displayName: string;
  address: string;
  fromName?: string;
  replyTo?: string;
  bccCopyAddress?: string;
  outboundProvider?: string;
  outboundEnabled?: boolean;
  smtp?: EmailSmtpSettingsInput;
  api?: EmailApiSettingsInput;
  inboundMode?: string;
  inboundEnabled?: boolean;
  imap?: EmailImapSettingsInput;
  inboundWebhookSecret?: string;
  behaviour?: EmailBehaviourSettingsInput;
  filters?: EmailFilterSettingsInput;
  isDefault?: boolean;
  oauth?: EmailOAuthSettingsInput;
}

export interface UpdateEmailChannelAccountCredentialsRequest {
  address?: string;
  fromName?: string;
  replyTo?: string;
  bccCopyAddress?: string;
  outboundProvider?: string;
  outboundEnabled?: boolean;
  smtp?: EmailSmtpSettingsInput;
  api?: EmailApiSettingsInput;
  inboundMode?: string;
  inboundEnabled?: boolean;
  imap?: EmailImapSettingsInput;
  inboundWebhookSecret?: string;
  behaviour?: EmailBehaviourSettingsInput;
  filters?: EmailFilterSettingsInput;
  oauth?: EmailOAuthSettingsInput;
}

export interface EmailProviderDescriptor {
  id: string;
  label: string;
  kind: 'smtp' | 'api';
  requiresDomain: boolean;
  defaultBaseUrl?: string | null;
  docsUrl?: string | null;
}

export interface EmailMailboxPreset {
  id: string;
  label: string;
  smtpHost?: string | null;
  smtpPort?: number | null;
  smtpSecurity?: string | null;
  imapHost?: string | null;
  imapPort?: number | null;
  imapSecurity?: string | null;
  note?: string | null;
}

export interface EmailProviderCatalog {
  providers: EmailProviderDescriptor[];
  presets: EmailMailboxPreset[];
  securityModes: string[];
  inboundModes: string[];
  replyFormats: string[];
}

export interface EmailSendResult {
  succeeded: boolean;
  messageId?: string | null;
  failureReason?: string | null;
}

export interface CallingConnectionTestResult {
  succeeded: boolean;
  message?: string | null;
  testedAtUtc: string;
}
